import { useCallback, useState } from 'react';
import SectionWrapper from './SectionWrapper';
import {
  Text,
  Box,
  Divider,
  ListItem,
  UnorderedList,
  FormControl,
  FormErrorMessage,
  Collapse,
} from '@chakra-ui/react';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import TemplateContentField from './TemplateContentField';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  addField,
  reorderField,
  setLoyaltyBalance,
} from '../../../store/redux/slices/template.slice';
import SwitchField from './SwitchField';
import BalanceNumberTypeRadio from './BalanceNumberTypeRadio';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';
import {
  FIELD_CONFIG,
  SLOT_LABELS,
  SLOT_ORDER,
  FRONT_FIELD_SECTIONS,
  FrontFieldSectionKey,
} from './utils/fieldVisibilityConfig';
import {
  useTemplateFieldValidation,
  useInitialDuplicateValidation,
} from './validation/useTemplateFieldValidation';
import AddMoreButton from './AddMoreButton';
import StrictModeDroppable from './utils/StrictModeDroppable';
import { useEnhancedDesignForTemplate } from './hooks/useEnhancedDesignForTemplate';

const SECTION_TITLES: Record<string, string> = {
  header: 'Header field',
  central: 'Central field',
  details: 'Detail field',
};

const KeyFieldsSection = ({ isEdit }: { isEdit: boolean }) => {
  const dispatch = useAppDispatch();
  const templateType = useAppSelector((s) => s.template.templateType);
  const loyaltyBalance = useAppSelector((s) => s.template.loyaltyBalance);
  const fields = useAppSelector((s) => s.template.fields);
  const config = FIELD_CONFIG[templateType];
  const enhancedDesign = useEnhancedDesignForTemplate();
  const showEnhancedFieldsSubtitle =
    enhancedDesign.available && enhancedDesign.storedDesign === 'generic';
  const maxFieldCount =
    config.header.limit + config.central.limit + config.details.limit;
  const { validateField, handleFocus } = useTemplateFieldValidation('fields');
  const errors = useAppSelector((s) => s.templateErrors);

  useInitialDuplicateValidation(
    [...FRONT_FIELD_SECTIONS],
    fields,
    validateField
  );

  const [isDragging, setIsDragging] = useState(false);
  const [dragSourceSection, setDragSourceSection] = useState<string | null>(
    null
  );

  // Track open/closed state per field so it follows the field data during drag reorder
  const [openFields, setOpenFields] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const sectionKey of FRONT_FIELD_SECTIONS) {
      const sectionFields = fields[sectionKey] || {};
      for (const slot of SLOT_ORDER) {
        if (sectionFields[slot] != null) {
          initial[`${sectionKey}.${slot}`] = !isEdit;
        }
      }
    }
    return initial;
  });

  const getFieldOpen = useCallback(
    (section: string, slot: string) =>
      openFields[`${section}.${slot}`] ?? !isEdit,
    [openFields, isEdit]
  );

  const setFieldOpen = useCallback(
    (section: string, slot: string, value: boolean) => {
      setOpenFields((prev) => ({ ...prev, [`${section}.${slot}`]: value }));
    },
    []
  );

  const handleDragStart = (start: { source: { droppableId: string } }) => {
    setIsDragging(true);
    setDragSourceSection(start.source.droppableId);
  };

  const handleDragEnd = (result: DropResult) => {
    setIsDragging(false);
    setDragSourceSection(null);
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const section = result.source.droppableId as FrontFieldSectionKey;

    // Reorder the open states to follow the field data
    const sectionFields = fields[section] || {};
    const activeSlots = SLOT_ORDER.filter((s) => sectionFields[s] != null);
    const openValues = activeSlots.map((s) => getFieldOpen(section, s));
    const [movedOpen] = openValues.splice(result.source.index, 1);
    openValues.splice(result.destination.index, 0, movedOpen);

    setOpenFields((prev) => {
      const next = { ...prev };
      // Clear old keys then write to post-reorder contiguous slots
      activeSlots.forEach((s) => {
        delete next[`${section}.${s}`];
      });
      openValues.forEach((val, i) => {
        next[`${section}.${SLOT_ORDER[i]}`] = val;
      });
      return next;
    });

    dispatch(
      reorderField({
        section,
        fromIndex: result.source.index,
        toIndex: result.destination.index,
      })
    );
  };

  const isStampCard = templateType === 'stampCard';

  return (
    <SectionWrapper
      title="Key fields"
      id="fields-section"
      tooltip={
        <Text textAlign="left">
          These are the fields shown on the front of your Apple Wallet pass and
          at the top of the pass view on Google Wallet. Each field can include:
          <UnorderedList>
            <ListItem>
              a label that&apos;s shown on the pass – optional
            </ListItem>
            <ListItem>
              a default value for static content that doesn&apos;t change
              between users – optional
            </ListItem>
          </UnorderedList>
        </Text>
      }
      subtitle={
        showEnhancedFieldsSubtitle
          ? `Add up to ${maxFieldCount} fields to display information on your pass. On iOS 27, fields 4 and above appear in a tappable list below.`
          : undefined
      }
      openOnLoad
    >
      {/* Loyalty balance toggle + radio (header section, non-stamp) */}
      {!isStampCard && config.header.limit > 0 && (
        <Box mb={2}>
          <FormControl
            mb={loyaltyBalance.enabled ? 4 : 2}
            isInvalid={
              !!errors['loyaltyBalance.enabled'] ||
              !!errors['loyaltyBalance.numberType']
            }
          >
            <SwitchField
              value={loyaltyBalance.enabled}
              onChange={(value: boolean) => {
                dispatch(setLoyaltyBalance({ enabled: value }));
                handleFocus('loyaltyBalance.enabled');
              }}
              label={
                <Text fontSize="16px" fontWeight="500">
                  Enable loyalty balance
                </Text>
              }
            />
          </FormControl>
          <Collapse in={loyaltyBalance.enabled}>
            <FormControl
              mt={2}
              isInvalid={!!errors['loyaltyBalance.numberType']}
            >
              <BalanceNumberTypeRadio
                value={loyaltyBalance.numberType}
                onChange={(value) =>
                  dispatch(setLoyaltyBalance({ numberType: value }))
                }
                onFocus={() => handleFocus('loyaltyBalance.numberType')}
              />
              {errors['loyaltyBalance.numberType'] && (
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {errors['loyaltyBalance.numberType']}
                </FormErrorMessage>
              )}
              {errors['loyaltyBalance.enabled'] && (
                <FormErrorMessage
                  pl={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {errors['loyaltyBalance.enabled']}
                </FormErrorMessage>
              )}
            </FormControl>
          </Collapse>
          <Divider borderColor="lightGrey" my={4} />
        </Box>
      )}

      {/* Field sections with drag-and-drop */}
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {FRONT_FIELD_SECTIONS.map((sectionKey, sectionIndex) => {
          const limit = config[sectionKey].limit;
          const fieldEntries = Object.entries(fields[sectionKey] || {}).filter(
            ([, field]) => field !== null
          );
          const isLimitReached = fieldEntries.length >= limit;
          const minRequired = config[sectionKey].minRequired;
          const isMinRequiredReached = fieldEntries.length > minRequired;
          const isDraggable = fieldEntries.length > 1;
          const sectionTitle =
            SECTION_TITLES[sectionKey] || `${sectionKey} field`;

          if (limit === 0) return null;

          return (
            <Box key={sectionKey}>
              {sectionIndex > 0 && <Divider borderColor="lightGrey" my={4} />}
              <StrictModeDroppable droppableId={sectionKey} type={sectionKey}>
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={
                      isDragging && dragSourceSection === sectionKey
                        ? {
                            cursor: 'grabbing',
                            '& *': {
                              cursor: 'grabbing !important',
                            },
                          }
                        : isDragging && dragSourceSection !== sectionKey
                          ? {
                              cursor: 'not-allowed',
                              '& *': {
                                cursor: 'not-allowed !important',
                              },
                            }
                          : undefined
                    }
                  >
                    {fieldEntries.map(([slot, field], fieldIndex) => (
                      <Box key={`${sectionKey}-${slot}`}>
                        {fieldIndex > 0 && !isDragging && (
                          <Divider borderColor="lightGrey" my={4} />
                        )}
                        <Draggable
                          draggableId={`${sectionKey}-${slot}`}
                          index={fieldIndex}
                          isDragDisabled={!isDraggable}
                        >
                          {(dragProvided) => (
                            <Box
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                            >
                              <TemplateContentField
                                label={
                                  isStampCard &&
                                  sectionKey === 'header' &&
                                  slot === 'one'
                                    ? 'Stamp Field'
                                    : loyaltyBalance.enabled &&
                                        sectionKey === 'header' &&
                                        slot === 'one'
                                      ? 'Balance Field'
                                      : `${sectionTitle} ${SLOT_LABELS[slot] ?? slot}`
                                }
                                section={sectionKey as FrontFieldSectionKey}
                                slot={slot}
                                newlyAdded={field._newlyAdded}
                                validateField={validateField}
                                errors={errors}
                                isMinRequiredReached={isMinRequiredReached}
                                isEdit={isEdit}
                                autoGenerateFieldId
                                isDraggable={isDraggable}
                                dragHandleProps={dragProvided.dragHandleProps}
                                controlledOpen={getFieldOpen(sectionKey, slot)}
                                onOpenChange={(value) =>
                                  setFieldOpen(sectionKey, slot, value)
                                }
                              />
                            </Box>
                          )}
                        </Draggable>
                      </Box>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </StrictModeDroppable>
              {!isLimitReached && (
                <Box mt={4}>
                  {fieldEntries.length > 0 && (
                    <Divider borderColor="lightGrey" mb={4} />
                  )}
                  <AddMoreButton
                    onClick={() => {
                      // Find the next available slot so we can mark it as open
                      const nextSlot = SLOT_ORDER.find(
                        (s) => fields[sectionKey]?.[s] == null
                      );
                      if (nextSlot) {
                        setFieldOpen(sectionKey, nextSlot, true);
                      }
                      dispatch(
                        addField({
                          section: sectionKey as FrontFieldSectionKey,
                        })
                      );
                    }}
                    text={`Add ${sectionKey.toLowerCase()} field`}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </DragDropContext>
    </SectionWrapper>
  );
};

export default KeyFieldsSection;
